import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useGameStore } from '@/hooks/useGameState';
import { VirtualDPad } from './VirtualDPad';

export const HUD = () => {
  const { t } = useTranslation();
  const playerSnake = useGameStore((state) => state.playerSnake);
  const aiSnakes = useGameStore((state) => state.aiSnakes);
  const isMuted = useGameStore((state) => state.isMuted);
  const setMuted = useGameStore((state) => state.setMuted);
  const setVirtualDirections = useGameStore((state) => state.setVirtualDirections);

  const handleDirectionChange = useCallback((directions: { up: boolean; down: boolean; left: boolean; right: boolean }) => {
    setVirtualDirections(directions);
  }, [setVirtualDirections]);

  if (!playerSnake) return null;

  // Build live leaderboard - all snakes sorted by score
  const allSnakesSorted = [
    { name: playerSnake.name, score: playerSnake.score, isPlayer: true },
    ...aiSnakes.map(s => ({ name: s.name, score: s.score, isPlayer: false }))
  ].sort((a, b) => b.score - a.score);

  // Calculate player rank from ALL snakes (not just top 5)
  const playerRank = allSnakesSorted.findIndex(s => s.isPlayer) + 1;

  // Only show top 5 in leaderboard display
  const leaderboardSnakes = allSnakesSorted.slice(0, 5);

  return (
    <>
      {/* Score - Bottom Left */}
      <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 hud-panel">
        <div className="text-xs text-muted-foreground tracking-wider">{t('hud.mass')}</div>
        <div className="text-2xl sm:text-3xl font-bold text-primary neon-text">
          {playerSnake.score}
        </div>
      </div>

      {/* Leaderboard - Top Right (hidden on small mobile) */}
      <div className="absolute top-6 right-6 hud-panel min-w-[140px] hidden sm:block">
        <h3 className="text-xs text-muted-foreground tracking-wider mb-2">{t('hud.liveTop5')}</h3>
        <div className="space-y-1">
          {leaderboardSnakes.map((snake, index) => (
            <div
              key={index}
              className={`flex justify-between text-xs ${
                snake.isPlayer ? 'text-primary font-bold' : 'text-muted-foreground'
              }`}
            >
              <span>{index + 1}. {snake.name}</span>
              <span>{snake.score}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Player Rank - Top Center */}
      <div className="absolute top-4 sm:top-6 left-1/2 -translate-x-1/2 text-center">
        <div className="text-xs text-muted-foreground tracking-wider">{t('hud.rank')}</div>
        <div className="text-xl sm:text-2xl font-bold text-primary neon-text">
          #{playerRank}
        </div>
      </div>

      {/* Mute Toggle - Top Left */}
      <button
        onClick={() => setMuted(!isMuted)}
        className="absolute top-4 sm:top-6 left-4 sm:left-6 text-xl sm:text-2xl hover:scale-110 transition-transform z-10"
        title={isMuted ? t('hud.unmute') : t('hud.mute')}
      >
        {isMuted ? '🔇' : '🔊'}
      </button>

      {/* Crosshair - Center */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        <div className="w-6 h-6 border border-primary/50 rounded-full flex items-center justify-center">
          <div className="w-1 h-1 bg-primary rounded-full" />
        </div>
      </div>

      {/* Boost Indicator */}
      {playerSnake.isBoosting && (
        <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 text-secondary text-xs sm:text-sm font-bold tracking-wider animate-pulse">
          {t('hud.boosting')}
        </div>
      )}

      {/* Invincibility Indicator */}
      {playerSnake.invincible && (
        <div className="absolute top-16 sm:top-20 left-1/2 -translate-x-1/2 text-accent text-xs sm:text-sm font-bold tracking-wider animate-pulse">
          {t('hud.spawnProtection')}
        </div>
      )}

      {/* Mobile Controls */}
      {/* Boost Button - Bottom Left, next to MASS display */}
      <div className="md:hidden absolute bottom-4 left-28 sm:left-32">
        <button
          className="w-14 h-14 rounded-full bg-secondary/20 border-2 border-secondary text-secondary font-bold text-xs flex items-center justify-center active:bg-secondary/40 active:scale-95 transition-all touch-none select-none"
          onTouchStart={(e) => {
            e.preventDefault();
            useGameStore.getState().setBoosting(true);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            useGameStore.getState().setBoosting(false);
          }}
        >
          {t('hud.boost')}
        </button>
      </div>

      {/* Virtual D-Pad - Bottom Right */}
      <div className="md:hidden absolute bottom-4 right-4">
        <VirtualDPad onDirectionChange={handleDirectionChange} />
      </div>
    </>
  );
};
